<script>
	import "./main.css";
	import { onMount } from "svelte";

	const IFC_LOGO_IMAGE = "/hillneighborhood.jpg";
	const BOULDER_HERO_IMAGE = "/boulder_flatirons_span.jpg";
	const BOULDER_NEIGHBORHOOD_IMAGE = "/boulder_thehill.jpg";
	const IFC_HOUSE_IMAGE = "/boulder_house_greenday.png";

	const backendURL = "https://slagbackend-e80f012e83b9.herokuapp.com";

	async function api(path, options = {}) {
		const isFormData = options.body instanceof FormData;

		const headers = {
			...(options.headers || {}),
		};

		if (!isFormData && !headers["Content-Type"]) {
			headers["Content-Type"] = "application/json";
		}

		const res = await fetch(`${backendURL}${path}`, {
			...options,
			headers,
		});
		return res;
	}

	// Routing
	let view = "site";
	let page = "home";

	function setPage(newPage) {
		view = "site";
		page = newPage;
		const path =
			newPage === "home" ? "/" : newPage === "about" ? "/about" : "/join";
		history.pushState({}, "", path);
		
		// Update body data attribute for mobile bar visibility
		if (typeof document !== "undefined") {
			document.body.setAttribute("data-page", newPage);
		}
	}

	function setAdminView() {
		view = "admin";
		history.pushState({}, "", "/submissions");
		if (typeof document !== "undefined") {
			document.body.setAttribute("data-page", "admin");
		}
	}

	onMount(() => {
  if (typeof window === "undefined") return;

  // --- Dynamic viewport vars (handles resize/orientation + iOS visual viewport) ---
  const applyViewportVars = () => {
    const vv = window.visualViewport;
    const w = vv?.width ?? window.innerWidth;
    const h = vv?.height ?? window.innerHeight;

    const root = document.documentElement;
    root.style.setProperty("--vw", `${w / 100}px`);
    root.style.setProperty("--vh", `${h / 100}px`);
    root.style.setProperty("--app-height", `${h}px`);
  };

  let raf = 0;
  const scheduleViewportUpdate = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      applyViewportVars();
    });
  };

  applyViewportVars();

  window.addEventListener("resize", scheduleViewportUpdate, { passive: true });
  window.addEventListener("orientationchange", scheduleViewportUpdate, { passive: true });
  window.visualViewport?.addEventListener("resize", scheduleViewportUpdate, { passive: true });
  window.visualViewport?.addEventListener("scroll", scheduleViewportUpdate, { passive: true });

  // --- Existing routing logic ---
  const syncRoute = () => {
    const path = window.location.pathname;
    if (path === "/about") {
      view = "site";
      page = "about";
    } else if (path === "/join") {
      view = "site";
      page = "join";
    } else if (path === "/submissions") {
      view = "admin";
    } else {
      view = "site";
      page = "home";
    }

    document.body.setAttribute("data-page", page);
  };

  syncRoute();
  window.onpopstate = syncRoute;

  return () => {
    window.removeEventListener("resize", scheduleViewportUpdate);
    window.removeEventListener("orientationchange", scheduleViewportUpdate);
    window.visualViewport?.removeEventListener("resize", scheduleViewportUpdate);
    window.visualViewport?.removeEventListener("scroll", scheduleViewportUpdate);
    if (raf) cancelAnimationFrame(raf);
    window.onpopstate = null;
  };
});


	let expandedLease = {};
	let expandedDocs = {};

	function toggleLease(id) {
		expandedLease = { ...expandedLease, [id]: !expandedLease[id] };
	}

	function toggleDocs(id) {
		expandedDocs = { ...expandedDocs, [id]: !expandedDocs[id] };
	}

	function mapsUrl(address) {
		if (!address) return "#";
		return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
			address,
		)}`;
	}

	let submitName = "";
	let submitEmail = "";
	let submitPhone = "";
	let submitAddress = "";
	let submitResidentsCount = "";
	let submitLeaseText = "";
	let submitDocumentation = "";
	let submitStatus = "";
	let submitError = "";
	let submitting = false;

	let emailError = "";
	let phoneError = "";

	const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	function formatPhone(digits) {
		if (digits.length <= 3) {
			return digits;
		}
		if (digits.length <= 6) {
			return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
		}
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
			6,
			10,
		)}`;
	}

	function handleEmailInput(event) {
		const value = (event.target.value || "").trim();
		submitEmail = value;

		if (!value) {
			emailError = "";
		} else if (!EMAIL_REGEX.test(value)) {
			emailError = "Enter a valid email address.";
		} else {
			emailError = "";
		}
	}

	function handlePhoneInput(event) {
		const raw = event.target.value || "";
		const digits = raw.replace(/\D/g, "").slice(0, 10);
		submitPhone = formatPhone(digits);

		if (!digits.length) {
			phoneError = "";
		} else if (digits.length < 10) {
			phoneError = "Enter a 10-digit US phone number.";
		} else {
			phoneError = "";
		}
	}

	// Calculate submission progress
	$: submitProgress = calculateProgress();
	
	function calculateProgress() {
		let completed = 0;
		let total = 4;
		
		if (submitName.trim() && selectedAddress?.formatted) completed++;
		if (submitEmail.trim() && submitPhone.trim() && submitResidentsCount) completed++;
		if (submitLeaseText.trim()) completed++;
		if (submitDocumentation.trim()) completed++;
		
		return { completed, total };
	}

	async function submitForm() {
		submitStatus = "";
		submitError = "";

		if (emailError || phoneError) {
			submitError =
				"Please fix the email and phone fields before submitting.";
			return;
		}

		const name = submitName.trim();
		const address = selectedAddress?.formatted;
		const leaseText = submitLeaseText.trim();
		const documentation = submitDocumentation.trim();
		const email = submitEmail.trim();
		const phone = submitPhone.trim();
		const residentsCountRaw = String(submitResidentsCount ?? "").trim();

		if (!name || !address || !email || !phone) {
			submitError = "Name, address, email, and phone are required.";
			return;
		}

		const residentsInt = Number.parseInt(residentsCountRaw, 10);
		if (!Number.isFinite(residentsInt) || residentsInt <= 0 || residentsInt > 50) {
			submitError = "Please enter a valid number of residents (1–50).";
			return;
		}

		const phoneDigits = phone.replace(/\D/g, "");
		if (phoneDigits.length !== 10) {
			phoneError = "Enter a 10-digit US phone number.";
			submitError = "Please fix the phone field before submitting.";
			return;
		}

		if (!EMAIL_REGEX.test(email)) {
			emailError = "Enter a valid email address.";
			submitError = "Please fix the email field before submitting.";
			return;
		}

		const formData = new FormData();
		formData.append("name", name);
		formData.append("address", address);
		formData.append("email", email);
		formData.append("phone", phone);
		formData.append("residentsCount", String(residentsInt));
		formData.append("leaseText", leaseText);
		formData.append("documentation", documentation);

		if (leaseFile) {
			formData.append("leaseFile", leaseFile);
		}

		if (Array.isArray(photoEvidenceFiles) && photoEvidenceFiles.length > 0) {
			for (const f of photoEvidenceFiles) {
				formData.append("photoEvidence", f);
			}
		}

		submitting = true;
		try {
			const res = await api("/submissions", {
				method: "POST",
				body: formData,
			});

			const body = await res.json().catch(() => ({}));

			if (!res.ok) {
				submitError =
					body.error || "Failed to submit. Please try again.";
				submitting = false;
				return;
			}

			submitStatus =
				"Submission received. You've helped build the Hill Housing Advocacy Petition.";
			submitName = "";
			submitAddress = "";
			submitEmail = "";
			submitPhone = "";
			submitResidentsCount = "";
			submitLeaseText = "";
			submitDocumentation = "";
			emailError = "";
			phoneError = "";

			leaseFile = null;
			leaseFileUrl = "";
			photoEvidenceFiles = [];
			selectedAddress = null;
		} catch (err) {
			console.error(err);
			submitError = "Network error. Please try again.";
		} finally {
			submitting = false;
		}
	}

	let adminPassword = "";
	let adminError = "";
	let adminLoading = false;
	let submissions = [];
	let hasLoadedSubmissions = false;

	async function loadSubmissions() {
		adminError = "";
		submissions = [];
		hasLoadedSubmissions = false;

		const pw = adminPassword.trim();
		if (!pw) {
			adminError = "Password is required.";
			return;
		}

		adminLoading = true;
		try {
			const res = await fetch(
				`${backendURL}/submissions?password=${encodeURIComponent(pw)}`,
			);

			if (!res.ok) {
				const body = await res.json().catch(() => ({}));
				adminError =
					res.status === 401
						? body.error || "Incorrect password."
						: body.error || "Failed to load submissions.";
				return;
			}

			const data = await res.json();
			submissions = data.submissions || [];
			hasLoadedSubmissions = true;
		} catch (err) {
			console.error(err);
			adminError = "Network error while loading submissions.";
		} finally {
			adminLoading = false;
		}
	}

	function formatDateTime(dt) {
		if (!dt) return "";
		const d = new Date(dt);
		if (Number.isNaN(d.getTime())) return dt;
		return d.toLocaleString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "numeric",
			minute: "2-digit",
		});
	}

	let searchQuery = "";
	let searchResults = [];

	$: if (hasLoadedSubmissions) {
		const q = searchQuery.trim().toLowerCase();

		if (!q) {
			searchResults = [];
		} else {
			searchResults = submissions.filter((sub) => {
				const name = (sub.name || "").toLowerCase();
				const addr = (sub.address || "").toLowerCase();
				const doc = (sub.documentation || "").toLowerCase();
				const lease = (sub.leaseText || "").toLowerCase();

				return (
					name.includes(q) ||
					addr.includes(q) ||
					doc.includes(q) ||
					lease.includes(q)
				);
			});
		}
	}

	let addressSuggestions = [];
	let addressSearchTimeout;
	let selectedAddress = null;

	async function fetchAddressSuggestions(query) {
		addressSuggestions = [];
		const q = (query || "").trim();
		if (q.length < 3) return;

		try {
			const res = await api(
				`/address-search?q=${encodeURIComponent(q)}`,
				{ method: "GET" },
			);

			if (!res.ok) {
				console.error("address-search HTTP error:", res.status);
				return;
			}

			const data = await res.json().catch(() => ({ results: [] }));
			addressSuggestions = data.results || [];
		} catch (err) {
			console.error("address search error", err);
		}
	}

	function handleAddressInput(event) {
		submitAddress = event.target.value;
		selectedAddress = null;
		clearTimeout(addressSearchTimeout);
		addressSearchTimeout = setTimeout(
			() => fetchAddressSuggestions(submitAddress),
			150,
		);
	}

	function chooseAddress(suggestion) {
		submitAddress = suggestion.formatted;
		selectedAddress = suggestion;
		addressSuggestions = [];
	}

	let leaseFile = null;
	let leaseFileUrl = "";
	let leaseUploadError = "";

	let photoEvidenceFiles = [];
	let photoEvidenceError = "";

	function wordCount(str) {
		return str.trim() ? str.trim().split(/\s+/).length : 0;
	}

	function handleLeaseFileChange(event) {
		const file = event.target.files?.[0];
		leaseFile = file || null;
		leaseUploadError = "";
	}

	function handlePhotoEvidenceChange(event) {
		const files = Array.from(event.target.files || []);
		photoEvidenceFiles = [...photoEvidenceFiles, ...files].slice(0, 10);
		photoEvidenceError = "";
		event.target.value = "";
	}
</script>

<div class="app light">
	<!-- NAVBAR -->
	<header class="ifc-navbar">
		<div class="ifc-nav-inner">
			<div class="ifc-nav-left">
				<div class="ifc-logo-circle">
					<img
						src={IFC_LOGO_IMAGE}
						alt="Hill Housing Advisory"
						class="ifc-logo-img"
					/>
				</div>
				<div class="ifc-nav-titles">
					<div class="ifc-nav-title-main">STUDENT LIVING ADVOCACY GROUP</div>
					<div class="ifc-nav-title-sub">
						The Hill · Student Housing Transparency
					</div>
				</div>
			</div>

			<div class="ifc-nav-right">
				{#if view === "site"}
					<nav class="ifc-nav-tabs">
						<button
							class={`ifc-nav-tab ${page === "home" ? "ifc-nav-tab-active" : ""}`}
							on:click={() => setPage("home")}
						>
							Overview
						</button>
						<button
							class={`ifc-nav-tab ${page === "about" ? "ifc-nav-tab-active" : ""}`}
							on:click={() => setPage("about")}
						>
							Mission &amp; Petition
						</button>
						<button
							class={`ifc-nav-tab ${page === "join" ? "ifc-nav-tab-active" : ""}`}
							on:click={() => setPage("join")}
						>
							Submit / Get Help
						</button>
					</nav>
				{:else}
					<span class="ifc-nav-admin-label">
						Admin · Hill Housing Advisory
					</span>
				{/if}
			</div>
		</div>
	</header>

	<!-- MOBILE STICKY SUBMIT BAR (shown on home & about pages) -->
	{#if view === "site" && page !== "join"}
		<div class="mobile-submit-bar">
			<div class="mobile-submit-bar-inner">
				<div class="mobile-submit-bar-text">
					<h4 class="mobile-submit-bar-title">Share Your Housing Story</h4>
					<p class="mobile-submit-bar-subtitle">Help build the petition for better conditions</p>
				</div>
				<button class="button" type="button" on:click={() => setPage("join")}>
					Get Started
				</button>
			</div>
		</div>
	{/if}

	<!-- MAIN -->
	<main class="main">
		<div class={`main-inner ${view === "admin" ? "main-inner-admin" : ""}`}>
			{#if view === "site"}
				{#if page === "home"}
					<!-- HERO -->
					<section class="hero-section">
						<div class="hero-main">
							<div class="hero-pill">
								Student Living Advocacy Group 
							</div>
							<h1>
								Build a shared record of housing on The Hill.
							</h1>
							<p class="hero-subtitle">
								The Student Living Advocacy Group is a student-run system for logging real experiences with off-campus housing on The Hill — fraternity and sorority houses, co-ops, and regular rentals. We're building a petition of addresses and stories to put in front of landlords, property managers, and city representatives so that we can advocate for better living conditions.
							</p>

							<div class="hero-actions">
								<button
									class="button"
									type="button"
									on:click={() => setPage("join")}
								>
									Share your living situation
								</button>
								<button
									class="button ghost"
									type="button"
									on:click={() => setPage("about")}
								>
									How the advisory works
								</button>
							</div>

							<div class="hero-metrics">
								<div class="hero-metric">
									<span class="hero-metric-value">Hill-wide</span>
									<span class="hero-metric-label">
										Open to <span style="font-weight: 800;">any house or unit</span> on The Hill
									</span>
								</div>
								<div class="hero-metric">
									<span class="hero-metric-value">Private</span>
									<span class="hero-metric-label">
										Used for patterns &amp; petitions, not public callouts
									</span>
								</div>
								<div class="hero-metric">
									<span class="hero-metric-value">Action-focused</span>
									<span class="hero-metric-label">
										Findings presented directly to landlords and managers
									</span>
								</div>
							</div>
						</div>

						<aside class="hero-side-card">
							<div class="hero-image-wrap">
								<img
									src={BOULDER_HERO_IMAGE}
									alt="Boulder and the Flatirons"
									class="hero-image"
								/>
								<div class="hero-image-tag">
									Boulder, Colorado · The Hill neighborhood
								</div>
							</div>

							<h2>What you can submit</h2>
							<ul class="hero-list bullet-list">
								<li>
									Detailed descriptions of repeated safety, maintenance, or habitability issues.
								</li>
								<li>
									Lease clauses that feel predatory, confusing, or one-sided.
								</li>
								<li>
									Documentation: photos, emails, work orders, inspection notices.
								</li>
								<li>
									Notes on how it affected you, your roommates, or your organization.
								</li>
							</ul>
							<p class="hero-note">
								Submissions are reviewed by a small advisory team and aggregated into patterns, maps, and summaries that can be presented to landlords to push for change.
							</p>
						</aside>
					</section>

					<!-- HOW IT WORKS -->
					<section class="section-block">
						<div class="section-header">
							<h2>How the Student Living Advocacy Group works</h2>
							<p class="section-subtitle">
								Simple flow. Clear expectations. Built for fraternities, sororities, co-ops, and regular rental houses across The Hill.
							</p>
						</div>

						<div class="timeline-grid">
							<div class="timeline-step">
								<div class="timeline-dot">1</div>
								<h3>Submit your situation</h3>
								<p>
									Any Hill resident shares an address, summary of the lease, and documentation of conditions or disputes.
								</p>
							</div>
							<div class="timeline-step">
								<div class="timeline-dot">2</div>
								<h3>Tag &amp; track issues</h3>
								<p>
									The advisory team tags issues (safety, habitability, deposits, fees) and looks for repeated patterns by address and landlord.
								</p>
							</div>
							<div class="timeline-step">
								<div class="timeline-dot">3</div>
								<h3>Build the petition</h3>
								<p>
									Addresses and themes become part of a Hill housing petition — a concrete list of places where residents are asking for change.
								</p>
							</div>
							<div class="timeline-step">
								<div class="timeline-dot">4</div>
								<h3>Present findings</h3>
								<p>
									We use anonymized stories, counts, and maps to start conversations with landlords, management companies, and local partners.
								</p>
							</div>
						</div>
					</section>

					<!-- FOR HOUSES / FOR INDIVIDUALS -->
					<section class="section-block">
						<div class="two-column-grid">
							<div class="info-card">
								<h3>For houses &amp; chapters</h3>
								<ul class="bullet-list">
									<li>
										Track recurring problem properties affecting your members or roommates.
									</li>
									<li>
										Use real examples in risk-management, move-in meetings, and new-member education.
									</li>
									<li>
										Coordinate across multiple houses when the same landlord keeps causing issues.
									</li>
									<li>
										Bring data — not just anecdotes — to landlords and property managers.
									</li>
								</ul>
							</div>

							<div class="info-card">
								<h3>For individual residents</h3>
								<ul class="bullet-list">
									<li>
										Create a dated record of housing issues on The Hill.
									</li>
									<li>
										Learn what to look for in future leases and walkthroughs.
									</li>
									<li>
										Know that your story becomes part of a collective push for better conditions.
									</li>
									<li>
										Optionally ask for a follow-up from the advisory team.
									</li>
								</ul>
							</div>
						</div>
					</section>

					<!-- IMAGE BAND: THE HILL -->
					<section class="image-band">
						<img
							src={BOULDER_NEIGHBORHOOD_IMAGE}
							alt="Off-campus Boulder neighborhood near The Hill"
							class="image-band-img"
						/>
						<div class="image-band-overlay">
							<div class="image-band-label">
								The Hill · Off-campus neighborhood
							</div>
							<p>
								Most submissions come from the same few blocks. By putting them together, we can clearly show landlords where conditions need to change.
							</p>
						</div>
					</section>

					<!-- BOTTOM CTA -->
					<section class="section-block section-cta">
						<div class="section-cta-inner">
							<div>
								<h2>Have a Hill housing story?</h2>
								<p>
									Whether you live in a chapter house, a rental with friends, or a co-op, your experience adds weight to the petition.
								</p>
							</div>
							<button
								class="button"
								type="button"
								on:click={() => setPage("join")}
							>
								Start a submission
							</button>
						</div>
					</section>
				{:else if page === "about"}
					<!-- ABOUT / MISSION -->
					<section class="section-block">
						<div class="section-header">
							<h2>A real change in student living</h2>
							<p class="section-subtitle">
								This advisory grew out of the same stories repeated across The Hill: unsafe houses, confusing leases, and maintenance that only moves when someone yells loud enough.
							</p>
						</div>

						<div class="two-column-grid about-grid">
							<div class="info-card">
								<h3>Why we built this</h3>
								<p>
									Hill residents cycle through the same properties every few years. When one house has problems, it usually hits multiple groups of students over time. Until now, those stories stayed scattered between friends, group chats, and officers.
								</p>
								<p>
									Hill Housing Advisory is a way to collect those stories, tie them to addresses, and present them together as a clear ask for better housing.
								</p>
							</div>

							<div class="info-card">
								<h3>What this is not</h3>
								<ul class="bullet-list">
									<li>No public landlord blacklist.</li>
									<li>No social-media callout campaigns.</li>
									<li>
										No doxxing or posting of private information.
									</li>
									<li>
										No formal legal advice — we can point you toward tenant and legal resources.
									</li>
								</ul>
							</div>
						</div>
					</section>

					<section class="section-block">
						<div class="two-column-grid about-grid">
							<div class="info-card">
								<h3>How we use submissions</h3>
								<ul class="bullet-list">
									<li>
										Identify addresses with repeating safety or habitability concerns.
									</li>
									<li>
										Spot lease terms that keep showing up and may need to be challenged.
									</li>
									<li>
										Build anonymized maps, stats, and summaries for conversations with landlords and companies.
									</li>
									<li>
										Support houses when multiple residents are dealing with the same owner.
									</li>
								</ul>
							</div>

							<div class="info-card">
								<h3>Our principles</h3>
								<ul class="bullet-list">
									<li>Protect students from overpriced low quality living conditions.</li>
									<li>
										Organize issues as facts, instead of unanswered complaints.
									</li>
									<li>
										Treat submissions as confidential internal material.
									</li>
									<li>
										Use data to encourage real change in our community.
									</li>
								</ul>
							</div>
						</div>
					</section>

					<section class="section-block section-cta">
						<div class="section-cta-inner">
							<div>
								<h2>Want your house represented?</h2>
								<p>
									Officers, presidents, risk managers, and tenants can all encourage submissions from their house. More addresses and stories make the petition harder to ignore.
								</p>
							</div>
							<button
								class="button ghost"
								type="button"
								on:click={() => setPage("join")}
							>
								View the submission form
							</button>
						</div>
					</section>
				{:else}
					<!-- JOIN / SUBMIT PAGE -->
					<section class="section-block">
						<div class="section-header">
							<h2>Submit your situation</h2>
							<p class="section-subtitle">
								Use this form to log an off-campus housing experience on The Hill. Any resident can submit, whether you're in a fraternity, sorority, co-op, or a regular rental.
							</p>
						</div>

						<!-- SUBMISSION PROGRESS TRACKER -->
						<!-- <div class="submit-progress">
							<div class="submit-progress-title">Your Progress: {submitProgress.completed} of {submitProgress.total} sections</div>
							<div class="submit-progress-steps">
								<div class="submit-progress-step" class:active={submitProgress.completed >= 1}></div>
								<div class="submit-progress-step" class:active={submitProgress.completed >= 2}></div>
								<div class="submit-progress-step" class:active={submitProgress.completed >= 3}></div>
								<div class="submit-progress-step" class:active={submitProgress.completed >= 4}></div>
							</div>
						</div> -->

						<!-- EMPHASIS CARD -->
						<!-- <div class="submit-emphasis-card">
							<h3>🏠 Your Story Matters</h3>
							<p>Every submission strengthens the petition. Hill residents deserve safe, fairly-priced housing with responsive management. Share your experience to help make that happen.</p>
						</div> -->

						<div class="two-column-grid join-grid">
							<div class="info-card join-form-card">
								<h3>Submission form</h3>

								<div class="field">
									<label>Your name</label>
									<input
										class="input"
										placeholder="Full name"
										bind:value={submitName}
									/>
								</div>

								<div class="field">
									<label>Address of the property</label>
									<div class="address-input-wrap">
										<input
											class="input"
											placeholder="Start typing an address on The Hill"
											bind:value={submitAddress}
											on:input={handleAddressInput}
										/>
										{#if addressSuggestions.length > 0}
											<div class="address-suggestions">
												{#each addressSuggestions as s}
													<button
														type="button"
														class="address-suggestion-row"
														on:click={() => chooseAddress(s)}
													>
														<div class="address-suggestion-main">
															{s.formatted}
														</div>
														<div class="address-suggestion-sub">
															{s.context}
														</div>
													</button>
												{/each}
											</div>
										{/if}
									</div>
								</div>

								<div class="field">
									<label>Email</label>
									<input
										class="input"
										type="email"
										placeholder="you@example.com"
										bind:value={submitEmail}
										on:input={handleEmailInput}
									/>
									{#if emailError}
										<div class="login-error">
											{emailError}
										</div>
									{/if}
								</div>

								<div class="field">
									<label>Phone</label>
									<input
										class="input"
										type="tel"
										placeholder="(303) 555-0123"
										bind:value={submitPhone}
										on:input={handlePhoneInput}
									/>
									{#if phoneError}
										<div class="login-error">
											{phoneError}
										</div>
									{/if}
								</div>

								<div class="field">
									<label>Number of residents</label>
									<input
										class="input"
										type="number"
										min="1"
										max="50"
										step="1"
										placeholder="e.g., 4"
										bind:value={submitResidentsCount}
									/>
									<div class="help-text">
										How many people live in the unit.
									</div>
								</div>

								<div class="field">
									<label>
										Lease &amp; terms (optional)
									</label>
									<textarea
										class="input"
										rows="4"
										placeholder="Paste key clauses, summarize unusual terms, or link to a PDF (Google Drive, etc.)."
										bind:value={submitLeaseText}
									></textarea>
									<div class="help-text">
										Optional · {wordCount(submitLeaseText)} words · Please remove bank details, SSNs, or other extremely sensitive personal info before sharing.
									</div>
								</div>

								<div class="field">
									<label>Lease file (optional)</label>
									<input
										class="input"
										type="file"
										accept=".pdf,image/*"
										on:change={handleLeaseFileChange}
									/>
									{#if leaseUploadError}
										<div class="login-error">
											{leaseUploadError}
										</div>
									{/if}
									<div class="help-text">
										Optional: upload a copy of your lease (PDF or image). We'll link it to your submission.
									</div>
								</div>

								<div class="field">
									<label>Photo evidence (optional)</label>
									<input
										class="input"
										type="file"
										accept="image/*"
										multiple
										on:change={handlePhotoEvidenceChange}
									/>
									{#if photoEvidenceError}
										<div class="login-error">{photoEvidenceError}</div>
									{/if}
									{#if photoEvidenceFiles.length > 0}
										<div class="help-text">
											Selected: {photoEvidenceFiles.length} photo{photoEvidenceFiles.length === 1 ? "" : "s"}
										</div>
									{:else}
										<div class="help-text">
											Optional: upload pictures that support your report (up to 10).
										</div>
									{/if}
								</div>

								<div class="field">
									<label>Describe the situation (optional)</label>
									<textarea
										class="input"
										rows="5"
										placeholder="Maintenance issues, safety concerns, deposit disputes, timelines, emails with property managers, inspection notices, etc."
										bind:value={submitDocumentation}
									></textarea>
									<div class="help-text">
										Optional · {wordCount(submitDocumentation)} words
									</div>
								</div>

								{#if submitError}
									<div class="login-error">{submitError}</div>
								{/if}
								{#if submitStatus}
									<div class="inline-status inline-status-success">
										{submitStatus}
									</div>
								{/if}

								<div class="hero-actions submit-actions">
									<button
										class="button"
										type="button"
										on:click={submitForm}
										disabled={submitting || emailError || phoneError}
									>
										{submitting ? "Submitting…" : "Submit to the petition"}
									</button>
								</div>
							</div>

							<div class="info-card join-side-card">
								<div class="join-image-stack">
									<img
										src={IFC_HOUSE_IMAGE}
										alt="Students outside a house on The Hill"
										class="join-image-main"
									/>
									<div class="join-image-caption">
										A Boulder project, built by people who live on The Hill and want better housing.
									</div>
								</div>

								<h3>What makes a strong submission?</h3>
								<ul class="bullet-list">
									<li>
										Include dates: move-in, when issues started, response times.
									</li>
									<li>
										Mention who you spoke with (office, manager, maintenance, etc.).
									</li>
									<li>
										Be concrete: "no heat for 12 days in January" beats "they were terrible."
									</li>
									<li>
										Note how it affected you or your house: health, finances, academics, chapter operations, etc.
									</li>
								</ul>

								<h3 style="margin-top: 18px;">
									Who can see this?
								</h3>
								<p>
									Submissions are stored in a private database, viewable only through a password-protected admin page used for the Hill housing project. We share patterns, counts, and anonymized stories — not full raw submissions.
								</p>

								<h3 style="margin-top: 18px;">
									Need direct help?
								</h3>
								<p>
									If you're in an active dispute, also reach out to your house leadership, trusted advisers, or local tenant / legal aid resources. This form is meant to support that process, not replace it.
								</p>
							</div>
						</div>
					</section>
				{/if}
			{:else}
				<!-- ADMIN VIEW -->
				{#if !hasLoadedSubmissions}
					<section class="section-block">
						<div class="info-card admin-login-card admin-login-centered">
							<div class="field admin-password-field">
								<label>Admin password</label>
								<input
									type="password"
									class="input admin-password-input"
									bind:value={adminPassword}
									on:keydown={(e) =>
										e.key === "Enter" && loadSubmissions()}
								/>
							</div>

							{#if adminError}
								<div class="login-error">{adminError}</div>
							{/if}

							<div class="hero-actions submit-actions admin-password-actions">
								<button
									class="button"
									type="button"
									on:click={loadSubmissions}
									disabled={adminLoading}
								>
									{adminLoading ? "Loading…" : "Sign in"}
								</button>
							</div>
						</div>
					</section>
				{:else}
					<section class="section-block admin-section-wide">
						<div class="section-header">
							<h2>Hill Housing Advisory · Admin</h2>
							<p class="section-subtitle">
								Password-protected overview of submissions. Use this only for advisory work and landlord-facing briefings.
							</p>
						</div>

						<div class="admin-grid">
							<div class="info-card admin-login-card">
								<h3>Search submissions</h3>

								<div class="field">
									<label>Search by name or address</label>
									<div class="admin-search-input-wrap">
										<input
											class="input admin-search-input"
											placeholder="Start typing a name or address"
											bind:value={searchQuery}
										/>
										<button
											type="button"
											class="search-icon-button"
											aria-label="Search"
										>
											<svg
												width="16"
												height="16"
												viewBox="0 0 24 24"
												aria-hidden="true"
											>
												<circle
													cx="11"
													cy="11"
													r="6"
													stroke="currentColor"
													stroke-width="2"
													fill="none"
												/>
												<line
													x1="16"
													y1="16"
													x2="21"
													y2="21"
													stroke="currentColor"
													stroke-width="2"
												/>
											</svg>
										</button>
									</div>
								</div>

								{#if hasLoadedSubmissions && searchQuery.trim() && searchResults.length === 0}
									<p class="help-text">
										No matching submissions found.
									</p>
								{:else if searchResults.length > 0}
									<div class="admin-search-results">
										{#each searchResults.slice(0, 5) as sub}
											<div class="search-result-row">
												<div class="search-result-main">
													<strong>
														{#if sub.address}
															<a
																href={mapsUrl(sub.address)}
																target="_blank"
																rel="noopener noreferrer"
															>
																{sub.address}
															</a>
														{:else}
															Unknown address
														{/if}
													</strong>

													<div class="search-result-meta">
														{sub.name || "Unknown name"}
														{#if sub.submissionNumber}
															&nbsp;· #{sub.submissionNumber}
														{/if}
														{#if sub.createdAt}
															&nbsp;· {formatDateTime(sub.createdAt)}
														{/if}
													</div>
												</div>
												<div class="search-result-snippet">
													{#if sub.documentation}
														{sub.documentation.slice(0, 140)}
														{sub.documentation.length > 140 ? "…" : ""}
													{:else if sub.leaseText}
														{sub.leaseText.slice(0, 140)}
														{sub.leaseText.length > 140 ? "…" : ""}
													{:else}
														No notes on file.
													{/if}
												</div>
											</div>
										{/each}
									</div>
								{/if}
							</div>

							<div class="info-card admin-table-card">
								<h3>Submissions</h3>

								{#if submissions.length === 0}
									<p class="help-text">
										No submissions yet. Once residents start logging situations, they will appear here.
									</p>
								{:else}
									<div class="admin-table-wrap">
										<table class="admin-table">
											<thead>
												<tr>
													<th>When</th>
													<th>Address</th>
													<th>#</th>
													<th>Name</th>
													<th>Email</th>
													<th>Phone</th>
													<th>Residents</th>
													<th>Lease notes</th>
													<th>Lease file</th>
													<th>Photo evidence</th>
													<th>Situation summary</th>
												</tr>
											</thead>

											<tbody>
												{#each submissions as sub}
													<tr>
														<td>{formatDateTime(sub.createdAt)}</td>

														<td>
															{#if sub.address}
																<a
																	href={mapsUrl(sub.address)}
																	target="_blank"
																	rel="noopener noreferrer"
																>
																	{sub.address}
																</a>
															{:else}
																—
															{/if}
														</td>

														<td>{sub.submissionNumber ?? "—"}</td>
														<td>{sub.name}</td>
														<td>{sub.email || "—"}</td>
														<td>{sub.phone || "—"}</td>
														<td>{sub.residentsCount ?? "—"}</td>

														<td>
															{#if sub.leaseText}
																{#if expandedLease[sub.id]}
																	{sub.leaseText}
																{:else}
																	{sub.leaseText.slice(0, 120)}
																	{#if sub.leaseText.length > 120}
																		…
																		<button
																			type="button"
																			class="link-button small"
																			on:click={() =>
																				toggleLease(sub.id)}
																		>
																			more
																		</button>
																	{/if}
																{/if}
															{:else}
																—
															{/if}
														</td>

														<td>
															{#if sub.leaseFileUrl}
																<a
																	href={sub.leaseFileUrl}
																	target="_blank"
																	rel="noopener noreferrer"
																>
																	View file
																</a>
															{:else}
																—
															{/if}
														</td>

														<td>
															{#if sub.photoEvidenceUrls && sub.photoEvidenceUrls.length > 0}
																{#each sub.photoEvidenceUrls as url, i}
																	<a
																		href={url}
																		target="_blank"
																		rel="noopener noreferrer"
																	>
																		Photo {i + 1}
																	</a>
																	{#if i < sub.photoEvidenceUrls.length - 1}
																		<span class="muted"> · </span>
																	{/if}
																{/each}
															{:else}
																—
															{/if}
														</td>

														<td>
															{#if sub.documentation}
																{#if expandedDocs[sub.id]}
																	{sub.documentation}
																{:else}
																	{sub.documentation.slice(0, 120)}
																	{#if sub.documentation.length > 120}
																		…
																		<button
																			type="button"
																			class="link-button small"
																			on:click={() =>
																				toggleDocs(sub.id)}
																		>
																			more
																		</button>
																	{/if}
																{/if}
															{:else}
																—
															{/if}
														</td>
													</tr>
												{/each}
											</tbody>
										</table>
									</div>
								{/if}
							</div>
						</div>
					</section>
				{/if}
			{/if}
		</div>
	</main>

	{#if view === "site"}
		<footer class="footer muted footer-simple">
			<span>Hill Housing Advisory · </span>
			<button
				class="link-button"
				type="button"
				on:click={setAdminView}
				title="Admin submissions view"
			>
				Admin
			</button>
		</footer>
	{/if}
</div>
